import { Temporal } from '@js-temporal/polyfill'
import { ArrowUpRightIcon, PlayIcon, XIcon } from 'lucide-react'
import { AnimatePresence, m, useReducedMotion } from 'motion/react'
import type { components } from '@joyful/openapi/types.gen'
import {
  Box,
  Button,
  Card,
  IconButton,
  Stack,
  useFloatingCornerSlot,
} from '../../../../core'
import { useAnnouncementsMenuHintContext } from '../AnnouncementsMenuHintContext'
import { useAnnouncementsQuery } from '../hooks/useAnnouncementsQuery'
import { useUnseenAnnouncements } from '../hooks/useUnseenAnnouncements'

type Announcement = components['schemas']['AnnouncementRead']

const EmptyAnnouncements: Array<Announcement> = []
const STALE_ANNOUNCEMENT_DAYS = 14
const MAX_PEEK_CARDS = 2

export interface UnseenAnnouncementPanelProps {
  onViewMore: (announcement: Announcement) => void
}

function getUnseenAnnouncements({
  announcements,
  dismissedIds,
  lastSeenDate,
}: {
  announcements: ReadonlyArray<Announcement>
  dismissedIds: ReadonlyArray<string>
  lastSeenDate: string | null
}) {
  if (announcements.length === 0) {
    return EmptyAnnouncements
  }

  const staleCutoffDate = Temporal.Now.plainDateISO('America/New_York')
    .subtract({ days: STALE_ANNOUNCEMENT_DAYS })
    .toString()

  return announcements.filter((announcement) => {
    if (announcement.launch_date === null) {
      return false
    }

    if (announcement.launch_date < staleCutoffDate) {
      return false
    }

    if (announcement.is_targeted === false) {
      return false
    }

    if (dismissedIds.includes(announcement.id)) {
      return false
    }

    if (lastSeenDate === null) {
      return true
    }

    return announcement.launch_date > lastSeenDate
  })
}

export function UnseenAnnouncementPanel({
  onViewMore,
}: UnseenAnnouncementPanelProps) {
  const { data } = useAnnouncementsQuery()
  const { dismissAnnouncement, dismissedIds, lastSeenDate } =
    useUnseenAnnouncements()
  const hint = useAnnouncementsMenuHintContext()
  const shouldReduceMotion = useReducedMotion()

  const announcements = data?.announcements ?? EmptyAnnouncements
  const unseenAnnouncements = getUnseenAnnouncements({
    announcements,
    dismissedIds,
    lastSeenDate,
  })
  const frontAnnouncement = unseenAnnouncements.at(0) ?? null
  const peekCount = Math.min(
    Math.max(unseenAnnouncements.length - 1, 0),
    MAX_PEEK_CARDS,
  )

  const handleDismiss = (announcement: Announcement) => {
    dismissAnnouncement(announcement.id)
    const remainingUnseenCount = unseenAnnouncements.filter(
      (unseenAnnouncement) => {
        return unseenAnnouncement.id !== announcement.id
      },
    ).length
    if (remainingUnseenCount === 0 && hint.state.dismissedAt === null) {
      hint.actions.activateHint()
    }
  }

  const { ref, style } = useFloatingCornerSlot({
    corner: 'bottom-left',
    enabled: frontAnnouncement !== null,
    feature: 'announcement',
  })
  const stackMotion =
    shouldReduceMotion === true
      ? {}
      : {
          animate: { opacity: 1, x: 0 },
          exit: { opacity: 0.5, x: -200 },
          initial: { opacity: 0.5, x: -200 },
          transition: { duration: 0.2 },
        }

  return (
    <AnimatePresence>
      {frontAnnouncement !== null ? (
        <m.div
          key="unseen-announcement-stack"
          ref={ref}
          style={style}
          className="fixed w-80 overflow-visible pt-6"
          {...stackMotion}
        >
          <div className="relative overflow-visible">
            {peekCount === 2 ? (
              <div
                aria-hidden
                className="bg-tremor-background-muted pointer-events-none absolute inset-x-3 -top-6 bottom-0 rounded-md border-2 opacity-70 shadow-xl"
              />
            ) : null}
            {peekCount >= 1 ? (
              <div
                aria-hidden
                className="bg-tremor-background-muted pointer-events-none absolute inset-x-2 -top-3 bottom-0 rounded-md border-2 opacity-80 shadow-xl"
              />
            ) : null}

            <AnimatePresence initial={false} mode="popLayout">
              <Card
                asChild
                key={frontAnnouncement.id}
                size="sm"
                className="group relative z-10 overflow-hidden border-2 p-0 shadow-xl"
              >
                <m.div {...stackMotion}>
                  <UnseenAnnouncementCard
                    announcement={frontAnnouncement}
                    onDismiss={() => {
                      handleDismiss(frontAnnouncement)
                    }}
                    onViewMore={() => {
                      handleDismiss(frontAnnouncement)
                      onViewMore(frontAnnouncement)
                    }}
                  />
                </m.div>
              </Card>
            </AnimatePresence>
          </div>
        </m.div>
      ) : null}
    </AnimatePresence>
  )
}

function UnseenAnnouncementCard({
  announcement,
  onDismiss,
  onViewMore,
}: {
  announcement: Announcement
  onDismiss: () => void
  onViewMore: () => void
}) {
  return (
    <>
      {announcement.file_url ? (
        // significant visual difference from our Button component
        // oxlint-disable-next-line joyful/no-plain-button
        <button
          type="button"
          aria-label={`View more, ${announcement.title}`}
          className="group/media relative w-full cursor-pointer border-b"
          onClick={onViewMore}
        >
          {announcement.file_mime_type?.startsWith('video/') ? (
            <>
              <video
                src={announcement.file_url}
                muted
                playsInline
                preload="metadata"
                className="pointer-events-none h-auto max-h-72 w-full object-contain"
              />

              <div className="absolute inset-0 flex items-center justify-center bg-black/10 transition-colors duration-150 group-hover/media:bg-black/20">
                <div className="text-tremor-content-inverted bg-tremor-background-emphasis/80 flex size-10 items-center justify-center rounded-full">
                  <PlayIcon className="size-4 fill-current" />
                </div>
              </div>
            </>
          ) : (
            <img
              src={announcement.file_url}
              alt={announcement.title}
              className="h-auto max-h-72 w-full object-contain"
            />
          )}
        </button>
      ) : null}

      <Box padding="md">
        <Stack align="start" gap="md">
          <Stack
            align="start"
            direction="horizontal"
            gap="sm"
            justify="between"
            width="full"
          >
            <Stack align="start" gap="xs">
              <p className="text-tremor-content text-xs font-medium">{`What's new`}</p>
              <p className="text-tremor-content-strong text-sm font-medium">
                {announcement.title}
              </p>
            </Stack>

            <IconButton
              variant="ghost"
              size="xs"
              tooltip="Dismiss"
              onClick={onDismiss}
            >
              <XIcon className="size-3.5" />
            </IconButton>
          </Stack>

          <Button
            variant="outline"
            size="xs"
            endIcon={<ArrowUpRightIcon />}
            onClick={onViewMore}
          >
            {`View more`}
          </Button>
        </Stack>
      </Box>
    </>
  )
}
